import { cn } from "@/lib/utils";
import { screenshotUrl, siteUrl, type ShowcaseSite } from "@/lib/showcase-sites";

export interface SiteFrameProps {
  site: ShowcaseSite;
  /** Use the 640px variant for small cards. */
  size?: "lg" | "sm";
  eager?: boolean;
  className?: string;
  /** Wrap the frame in an outbound link to the live site. */
  link?: boolean;
}

/**
 * A browser-chrome frame around a production screenshot of one of the
 * sites running on delapresse. Purely presentational: no data fetch, no
 * customer content, only the captured image and the domain.
 */
export function SiteFrame({ site, size = "lg", eager = false, className, link = true }: SiteFrameProps) {
  const inner = (
    <>
      <div className="flex h-7 items-center gap-2 border-b border-rule bg-paper px-3">
        <span aria-hidden className="flex gap-1">
          <i className="block h-2 w-2 rounded-full bg-rule" />
          <i className="block h-2 w-2 rounded-full bg-rule" />
          <i className="block h-2 w-2 rounded-full bg-rule" />
        </span>
        <span className="ml-1 truncate text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{site.domain}</span>
      </div>
      <div className="aspect-[16/10] w-full overflow-hidden bg-muted">
        <img
          src={screenshotUrl(site, size)}
          alt={`${site.name} (${site.domain})`}
          width={size === "lg" ? 1200 : 640}
          height={size === "lg" ? 750 : 400}
          loading={eager ? "eager" : "lazy"}
          fetchPriority={eager ? "high" : undefined}
          decoding="async"
          className="h-full w-full object-cover object-top"
        />
      </div>
    </>
  );

  const frameClass = cn("block overflow-hidden border border-rule bg-background shadow-editorial", className);

  if (!link) return <div className={frameClass}>{inner}</div>;
  return (
    <a href={siteUrl(site)} target="_blank" rel="noopener noreferrer" className={frameClass} aria-label={site.name}>
      {inner}
    </a>
  );
}
